import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Typography,
    Box,
    IconButton,
    Tabs,
    Tab,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    Button,
    TextField,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Card,
    CardContent,
    Slider,
} from "@mui/material"
import {
    Close as CloseIcon,
    Settings as SettingsIcon,
    Palette as PaletteIcon,
    Notifications as NotificationsIcon,
    Security as SecurityIcon,
    Language as LanguageIcon,
    Storage as StorageIcon,
    Brightness4 as DarkModeIcon,
    Brightness7 as LightModeIcon,
    VolumeUp as VolumeIcon,
    Save as SaveIcon,
    RestartAlt as ResetIcon,
} from "@mui/icons-material"
import {useState} from "react"
import {useTranslation} from "react-i18next"

const SettingsDialog = ({open, onClose, onSave}) => {
    const {t, i18n} = useTranslation()
    const [tabValue, setTabValue] = useState(0)
    const [hasChanges, setHasChanges] = useState(false)
    const [saveStatus, setSaveStatus] = useState(null) // 'success', 'error', null

    // Settings state
    const [settings, setSettings] = useState({
        // Appearance
        theme: "system", // 'light', 'dark', 'system'
        language: i18n.language || "en",
        fontSize: "medium", // 'small', 'medium', 'large'
        compactMode: false,

        // Notifications
        emailNotifications: true,
        pushNotifications: true,
        orderUpdates: true,
        systemAlerts: true,
        marketingEmails: false,
        notificationSound: true,
        soundVolume: 70,

        // Privacy & Security
        twoFactorAuth: false,
        sessionTimeout: 30, // minutes
        showOnlineStatus: true,
        dataSharing: false,

        // Data & Storage
        autoSave: true,
        cacheSize: "100", // MB
        exportFormat: "excel", // 'excel', 'pdf', 'csv'
        dataRetention: 90, // days
    })

    const handleSettingChange = (key, value) => {
        setSettings((prev) => ({...prev, [key]: value}))
        setHasChanges(true)
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue)
    }

    const handleSave = async () => {
        try {
            setSaveStatus(null)
            // Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 1000))

            // Apply language change
            if (settings.language !== i18n.language) {
                i18n.changeLanguage(settings.language)
            }

            onSave?.(settings)
            setSaveStatus("success")
            setHasChanges(false)

            // Auto-hide success message
            setTimeout(() => setSaveStatus(null), 3000)
        } catch (error) {
            setSaveStatus("error")
            setTimeout(() => setSaveStatus(null), 5000)
        }
    }

    const handleReset = () => {
        setSettings({
            theme: "system",
            language: "en",
            fontSize: "medium",
            compactMode: false,
            emailNotifications: true,
            pushNotifications: true,
            orderUpdates: true,
            systemAlerts: true,
            marketingEmails: false,
            notificationSound: true,
            soundVolume: 70,
            twoFactorAuth: false,
            sessionTimeout: 30,
            showOnlineStatus: true,
            dataSharing: false,
            autoSave: true,
            cacheSize: "100",
            exportFormat: "excel",
            dataRetention: 90,
        })
        setHasChanges(true)
    }

    const TabPanel = ({children, value, index}) => (
        <div hidden={value !== index} style={{paddingTop: 16}}>
            {value === index && children}
        </div>
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {height: "80vh", maxHeight: 600},
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <SettingsIcon color="primary"/>
                    <Typography variant="h6">{t("Settings")}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <Divider/>

            <Box sx={{display: "flex", height: "100%"}}>
                {/* Sidebar Tabs */}
                <Box sx={{borderRight: 1, borderColor: "divider", minWidth: 200}}>
                    <Tabs orientation="vertical" value={tabValue} onChange={handleTabChange} sx={{p: 1}}>
                        <Tab
                            icon={<PaletteIcon/>}
                            iconPosition="start"
                            label={t("Appearance")}
                            sx={{justifyContent: "flex-start", minHeight: 48}}
                        />
                        <Tab
                            icon={<NotificationsIcon/>}
                            iconPosition="start"
                            label={t("Notifications")}
                            sx={{justifyContent: "flex-start", minHeight: 48}}
                        />
                        <Tab
                            icon={<SecurityIcon/>}
                            iconPosition="start"
                            label={t("Privacy & Security")}
                            sx={{justifyContent: "flex-start", minHeight: 48}}
                        />
                        <Tab
                            icon={<StorageIcon/>}
                            iconPosition="start"
                            label={t("Data & Storage")}
                            sx={{justifyContent: "flex-start", minHeight: 48}}
                        />
                    </Tabs>
                </Box>

                {/* Content Area */}
                <DialogContent sx={{flex: 1, p: 0}}>
                    {saveStatus && (
                        <Alert severity={saveStatus} sx={{m: 2, mb: 0}} onClose={() => setSaveStatus(null)}>
                            {saveStatus === "success"
                                ? t("Settings saved successfully!")
                                : t("Failed to save settings. Please try again.")}
                        </Alert>
                    )}

                    {/* Appearance Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                {t("Appearance")}
                            </Typography>

                            <Card variant="outlined" sx={{mb: 3}}>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Theme")}
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{mb: 2}}>
                                        <Select
                                            value={settings.theme}
                                            onChange={(e) => handleSettingChange("theme", e.target.value)}
                                            startAdornment={
                                                settings.theme === "dark" ? <DarkModeIcon sx={{mr: 1}}/> :
                                                    <LightModeIcon sx={{mr: 1}}/>
                                            }
                                            variant="outlined">
                                            <MenuItem value="light">{t("Light")}</MenuItem>
                                            <MenuItem value="dark">{t("Dark")}</MenuItem>
                                            <MenuItem value="system">{t("System")}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Language")}
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{mb: 2}}>
                                        <Select
                                            value={settings.language}
                                            onChange={(e) => handleSettingChange("language", e.target.value)}
                                            startAdornment={<LanguageIcon sx={{mr: 1}}/>}
                                            variant="outlined">
                                            <MenuItem value="en">English</MenuItem>
                                            <MenuItem value="hy">Հայերեն (Armenian)</MenuItem>
                                            <MenuItem value="ru">Русский (Russian)</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Font Size")}
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{mb: 2}}>
                                        <Select value={settings.fontSize}
                                                onChange={(e) => handleSettingChange("fontSize", e.target.value)}
                                                variant="outlined">
                                            <MenuItem value="small">{t("Small")}</MenuItem>
                                            <MenuItem value="medium">{t("Medium")}</MenuItem>
                                            <MenuItem value="large">{t("Large")}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.compactMode}
                                                onChange={(e) => handleSettingChange("compactMode", e.target.checked)}
                                            />
                                        }
                                        label={t("Compact Mode")}
                                    />
                                </CardContent>
                            </Card>
                        </Box>
                    </TabPanel>

                    {/* Notifications Tab */}
                    <TabPanel value={tabValue} index={1}>
                        <Box sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                {t("Notifications")}
                            </Typography>

                            <Card variant="outlined" sx={{mb: 3}}>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Email Notifications")}
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText
                                                primary={t("Email Notifications")}
                                                secondary={t("Receive notifications via email")}
                                            />
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.emailNotifications}
                                                    onChange={(e) => handleSettingChange("emailNotifications", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={t("Order Updates")}
                                                secondary={t("Get notified about order status changes")}
                                            />
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.orderUpdates}
                                                    onChange={(e) => handleSettingChange("orderUpdates", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary={t("System Alerts")}
                                                          secondary={t("Important system notifications")}/>
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.systemAlerts}
                                                    onChange={(e) => handleSettingChange("systemAlerts", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={t("Marketing Emails")}
                                                secondary={t("Promotional and marketing content")}
                                            />
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.marketingEmails}
                                                    onChange={(e) => handleSettingChange("marketingEmails", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Push Notifications")}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.pushNotifications}
                                                onChange={(e) => handleSettingChange("pushNotifications", e.target.checked)}
                                            />
                                        }
                                        label={t("Enable Push Notifications")}
                                        sx={{mb: 2}}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.notificationSound}
                                                onChange={(e) => handleSettingChange("notificationSound", e.target.checked)}
                                            />
                                        }
                                        label={t("Notification Sound")}
                                        sx={{mb: 2}}
                                    />

                                    {settings.notificationSound && (
                                        <Box sx={{px: 2}}>
                                            <Typography variant="body2" gutterBottom>
                                                {t("Sound Volume")}: {settings.soundVolume}%
                                            </Typography>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                                <VolumeIcon/>
                                                <Slider
                                                    value={settings.soundVolume}
                                                    onChange={(e, value) => handleSettingChange("soundVolume", value)}
                                                    min={0}
                                                    max={100}
                                                    sx={{flex: 1}}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    </TabPanel>

                    {/* Privacy & Security Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <Box sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                {t("Privacy & Security")}
                            </Typography>

                            <Card variant="outlined" sx={{mb: 3}}>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Security")}
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText
                                                primary={t("Two-Factor Authentication")}
                                                secondary={t("Add an extra layer of security to your account")}
                                            />
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.twoFactorAuth}
                                                    onChange={(e) => handleSettingChange("twoFactorAuth", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    </List>

                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Session Timeout")}
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{mt: 2}}>
                                        <Select
                                            value={settings.sessionTimeout}
                                            onChange={(e) => handleSettingChange("sessionTimeout", e.target.value)}
                                            variant="outlined">
                                            <MenuItem value={15}>15 {t("minutes")}</MenuItem>
                                            <MenuItem value={30}>30 {t("minutes")}</MenuItem>
                                            <MenuItem value={60}>1 {t("hour")}</MenuItem>
                                            <MenuItem value={120}>2 {t("hours")}</MenuItem>
                                            <MenuItem value={480}>8 {t("hours")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Privacy")}
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText
                                                primary={t("Show Online Status")}
                                                secondary={t("Let others see when you're online")}
                                            />
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.showOnlineStatus}
                                                    onChange={(e) => handleSettingChange("showOnlineStatus", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText
                                                primary={t("Data Sharing")}
                                                secondary={t("Share usage data to improve the service")}
                                            />
                                            <ListItemSecondaryAction>
                                                <Switch
                                                    checked={settings.dataSharing}
                                                    onChange={(e) => handleSettingChange("dataSharing", e.target.checked)}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Box>
                    </TabPanel>

                    {/* Data & Storage Tab */}
                    <TabPanel value={tabValue} index={3}>
                        <Box sx={{p: 3}}>
                            <Typography variant="h6" gutterBottom>
                                {t("Data & Storage")}
                            </Typography>

                            <Card variant="outlined" sx={{mb: 3}}>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Data Management")}
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.autoSave}
                                                onChange={(e) => handleSettingChange("autoSave", e.target.checked)}
                                            />
                                        }
                                        label={t("Auto-save changes")}
                                        sx={{mb: 2}}
                                    />

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t("Cache Size (MB)")}
                                        type="number"
                                        value={settings.cacheSize}
                                        onChange={(e) => handleSettingChange("cacheSize", e.target.value)}
                                        sx={{mb: 2}}
                                    />

                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Default Export Format")}
                                    </Typography>
                                    <FormControl fullWidth size="small" sx={{mb: 2}} >
                                        <Select
                                            value={settings.exportFormat}
                                            onChange={(e) => handleSettingChange("exportFormat", e.target.value)}
                                            variant="outlined">
                                            <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                                            <MenuItem value="pdf">PDF (.pdf)</MenuItem>
                                            <MenuItem value="csv">CSV (.csv)</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Data Retention")}
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={settings.dataRetention}
                                            onChange={(e) => handleSettingChange("dataRetention", e.target.value)}
                                            variant="outlined">
                                            <MenuItem value={30}>30 {t("days")}</MenuItem>
                                            <MenuItem value={90}>90 {t("days")}</MenuItem>
                                            <MenuItem value={180}>6 {t("months")}</MenuItem>
                                            <MenuItem value={365}>1 {t("year")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {t("Storage Info")}
                                    </Typography>
                                    <Box sx={{display: "flex", gap: 2, flexWrap: "wrap"}}>
                                        <Chip label={`${t("Cache")}: ${settings.cacheSize}MB`} variant="outlined"/>
                                        <Chip label={`${t("Documents")}: 245MB`} variant="outlined"/>
                                        <Chip label={`${t("Images")}: 89MB`} variant="outlined"/>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </TabPanel>
                </DialogContent>
            </Box>

            <Divider/>

            <DialogActions sx={{p: 2, justifyContent: "space-between"}}>
                <Button startIcon={<ResetIcon/>} onClick={handleReset} color="inherit">
                    {t("Reset to Defaults")}
                </Button>

                <Box sx={{display: "flex", gap: 1}}>
                    <Button onClick={onClose}>{t("Cancel")}</Button>
                    <Button variant="contained" startIcon={<SaveIcon/>} onClick={handleSave} disabled={!hasChanges}>
                        {t("Save Changes")}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default SettingsDialog
